<template>
  <div class="admin-inspection-plan-container animated fadeInRight">
    <el-row :gutter="16">
      <!-- 左侧：小区选择（保留原有纵向列表交互，仅卡片化） -->
      <el-col :span="4">
        <div class="ds-side-card">
          <div class="ds-side-card__title">{{ $t('adminInspectionPlan.community.all') }}</div>
          <select-admin-community ref="selectCommunity" @changeCommunity="handleCommunityChange" />
        </div>
      </el-col>
      <el-col :span="20">
        <!-- (1) PageHeader -->
        <ds-page-header :title="$t('adminInspectionPlan.page.title')" :subtitle="$t('adminInspectionPlan.page.subtitle')" />

        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="searchForm.inspectionPlanId" clearable
                :placeholder="$t('adminInspectionPlan.search.inspectionPlanId')" @keyup.enter.native="handleSearch" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="searchForm.adminInspectionPlanName" clearable
                :placeholder="$t('adminInspectionPlan.search.inspectionPlanName')" @keyup.enter.native="handleSearch" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="searchForm.staffNameLike" clearable
                :placeholder="$t('adminInspectionPlan.search.staffName')" @keyup.enter.native="handleSearch" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="searchForm.state" clearable class="w-full"
                :placeholder="$t('adminInspectionPlan.search.state')">
                <el-option v-for="item in stateOptions" :key="item.statusCd" :label="item.name" :value="item.statusCd" />
              </el-select>
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">
              {{ $t('common.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="handleReset">
              {{ $t('common.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="getList">
            <template #left>
              <span class="ds-panel__title">{{ $t('adminInspectionPlan.list.title') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('adminInspectionPlan.page.empty')" @retry="getList">
            <el-table-column prop="inspectionPlanName" :label="$t('adminInspectionPlan.table.planName')" align="left"
              min-width="160" show-overflow-tooltip />
            <el-table-column prop="communityName" :label="$t('adminInspectionPlan.table.communityName')" align="center"
              width="140" show-overflow-tooltip />
            <el-table-column prop="inspectionRouteName" :label="$t('adminInspectionPlan.table.inspectionRoute')"
              align="center" min-width="140" show-overflow-tooltip />
            <el-table-column prop="inspectionPlanPeriodName" :label="$t('adminInspectionPlan.table.planPeriod')"
              align="center" width="110" />
            <el-table-column prop="signTypeName" :label="$t('adminInspectionPlan.table.signType')" align="center"
              width="100" />
            <!-- 日期范围：起止两行，主次分明 -->
            <el-table-column :label="$t('adminInspectionPlan.table.dateRange')" align="center" width="170">
              <template slot-scope="scope">
                <div class="ds-num">{{ scope.row.startDate || '-' }}</div>
                <div class="ds-num ds-cell-sub">{{ scope.row.endDate || '-' }}</div>
              </template>
            </el-table-column>
            <!-- 时间范围：起止两行，主次分明 -->
            <el-table-column :label="$t('adminInspectionPlan.table.timeRange')" align="center" width="150">
              <template slot-scope="scope">
                <div class="ds-num">{{ scope.row.startTime || '-' }}</div>
                <div class="ds-num ds-cell-sub">{{ scope.row.endTime || '-' }}</div>
              </template>
            </el-table-column>
            <el-table-column prop="beforeTime" :label="$t('adminInspectionPlan.table.beforeTime')" align="right"
              width="110">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.beforeTime != null ? scope.row.beforeTime : '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="createUserName" :label="$t('adminInspectionPlan.table.creator')" align="center"
              width="100" />
            <el-table-column prop="createTime" :label="$t('adminInspectionPlan.table.createTime')" align="center"
              width="160" />
            <!-- 状态 Tag：颜色仅作辅助，始终带文字 -->
            <el-table-column prop="stateName" :label="$t('adminInspectionPlan.table.state')" align="center" width="110">
              <template slot-scope="scope">
                <ds-status-tag :type="resolveState(scope.row)" :label="scope.row.stateName || '-'" />
              </template>
            </el-table-column>
            <!-- 巡检人员：多人纵向排列 -->
            <el-table-column :label="$t('adminInspectionPlan.table.inspectionStaff')" align="center" min-width="120">
              <template slot-scope="scope">
                <div v-if="scope.row.staffs && scope.row.staffs.length">
                  <div v-for="(staff, i) in scope.row.staffs" :key="i">{{ staff.staffName }}</div>
                </div>
                <span v-else class="ds-cell-sub">-</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('common.operation')" align="center" width="100" fixed="right">
              <template slot-scope="scope">
                <el-button size="mini" type="text" @click="handleDetail(scope.row)">
                  {{ $t('common.detail') }}
                </el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
            :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
            <template #summary>
              <span class="ds-tip-text">{{ $t('adminInspectionPlan.tip.ensureValid') }}</span>
            </template>
          </ds-pagination-bar>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import SelectAdminCommunity from '@/components/community/selectAdminCommunity'
import { listAdminInspectionPlans } from '@/api/inspection/adminInspectionPlanApi'
import { getDict } from '@/api/community/communityApi'
import { resolveStatus } from '@/utils/statusMap'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'AdminInspectionPlanList',
  components: {
    SelectAdminCommunity,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        inspectionPlanId: '',
        adminInspectionPlanName: '',
        staffNameLike: '',
        state: '',
        communityId: ''
      },
      tableData: [],
      stateOptions: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.getStateOptions()
    this.getList()
  },
  methods: {
    async getStateOptions() {
      try {
        const res = await getDict('inspection_plan', 'state')
        // 修复：getDict 返回的是数组本体，原实现取 res.data 会得到 undefined，
        // 导致状态下拉恒为空。此处只改取值方式，不改接口与参数。
        this.stateOptions = Array.isArray(res) ? res : (res && Array.isArray(res.data) ? res.data : [])
      } catch (error) {
        this.$message.error(this.$t('adminInspectionPlan.error.fetchStateFailed'))
      }
    },
    async getList() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          ...this.searchForm,
          page: this.pagination.current,
          row: this.pagination.size
        }
        const res = await listAdminInspectionPlans(params)

        // 兼容后端在「无匹配数据」时返回 { code:0, records:0 } 而不带 data 字段的情况。
        this.tableData = Array.isArray(res.data) ? res.data : []
        this.pagination.total = res.total || 0
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('adminInspectionPlan.error.fetchListFailed')
        this.$message.error(this.$t('adminInspectionPlan.error.fetchListFailed'))
      } finally {
        this.loading = false
      }
    },
    handleCommunityChange(community) {
      this.searchForm.communityId = community.communityId
      this.getList()
    },
    handleSearch() {
      this.pagination.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm = {
        inspectionPlanId: '',
        adminInspectionPlanName: '',
        staffNameLike: '',
        state: '',
        communityId: this.searchForm.communityId
      }
      this.handleSearch()
    },
    handleDetail(row) {
      window.open(`/#/pages/inspection/aInspectionPlanDetail?inspectionPlanId=${row.inspectionPlanId}`)
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.pagination.current = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this.getList()
    },
    /**
     * 状态语义类型（巡检计划），未命中时按文案语义推断并安全降级。
     */
    resolveState(row) {
      if (!row) return 'muted'
      return resolveStatus(row.state, {
        domain: 'inspectionPlan',
        text: row.stateName || ''
      }).type
    }
  }
}
</script>

<style lang="scss" scoped>
.admin-inspection-plan-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 表格次要文字 */
.ds-cell-sub {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 左侧小区选择卡片 */
.ds-side-card {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    padding: var(--ds-spacing-sm) var(--ds-spacing-md);
    font-size: var(--ds-font-size-sm);
    font-weight: 600;
    color: var(--ds-gray-700);
    background: var(--ds-gray-100);
    border-bottom: 1px solid var(--ds-gray-300);
  }

  /* 内嵌的社区选择卡片去掉自身边框，避免双层边框 */
  ::v-deep .community-selector {
    border: none;
    box-shadow: none;
    border-radius: 0;
    max-height: calc(100vh - 260px);
  }
}
</style>
